// src/pages/admin/AdminValidacionPendientes.js
//
// Bandeja del validador (y superadmin) con los negocios que esperan
// revisión. Cada fila lleva al detalle de validación.
//
// Visible para SUPERADMIN y VALIDADOR. El listado usa la data del backend
// (guia_validacion_manual.md) que devuelve contadores de documentos y
// fotos del guía por cada negocio.

import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Card, CardContent, Typography, Table, TableHead, TableRow, TableCell,
  TableBody, Chip, Button, Alert, CircularProgress, Stack,
} from '@mui/material';
import RefreshIcon      from '@mui/icons-material/Refresh';
import AssignmentIcon   from '@mui/icons-material/Assignment';
import { validacion } from '../../api/authApi';
import { fechaLocal } from '../../utils/format';

const chipEstado = (estado) => ({
  PENDIENTE_VALIDACION: <Chip label="Pendiente"     color="warning" size="small" />,
  EN_REVISION:          <Chip label="En revisión"   color="info"    size="small" />,
  DOCS_RECHAZADOS:      <Chip label="Docs rechazados" color="error" size="small" />,
}[estado] || <Chip label={estado} size="small" />);

export default function AdminValidacionPendientes() {
  const navigate = useNavigate();
  const [data, setData]         = useState({ pendientes: [], total: 0 });
  const [cargando, setCargando] = useState(true);
  const [error, setError]       = useState('');

  const cargar = useCallback(async () => {
    setCargando(true); setError('');
    try {
      const r = await validacion.pendientes();
      setData(r);
    } catch (e) { setError(e?.message || 'No se pudo cargar la lista.'); }
    finally { setCargando(false); }
  }, []);

  useEffect(() => { cargar(); }, [cargar]);

  return (
    <Box p={3}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" mb={2}>
        <Stack direction="row" spacing={1} alignItems="center">
          <AssignmentIcon color="primary" />
          <Typography variant="h5" fontWeight={700}>Validación de negocios</Typography>
          <Chip label={`${data.total} pendientes`} color={data.total > 0 ? 'warning' : 'default'} />
        </Stack>
        <Button startIcon={<RefreshIcon />} onClick={cargar} disabled={cargando}>Refrescar</Button>
      </Stack>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

      <Card>
        <CardContent>
          {cargando ? (
            <Box textAlign="center" py={4}><CircularProgress /></Box>
          ) : data.pendientes.length === 0 ? (
            <Box textAlign="center" py={6}>
              <Typography color="text.secondary">
                No hay negocios esperando validación. 🎉
              </Typography>
            </Box>
          ) : (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Negocio</TableCell>
                  <TableCell>NIT</TableCell>
                  <TableCell>Guía</TableCell>
                  <TableCell>Estado</TableCell>
                  <TableCell align="center">Docs</TableCell>
                  <TableCell align="center">Docs pend</TableCell>
                  <TableCell align="center">Fotos guía</TableCell>
                  <TableCell>Registrado</TableCell>
                  <TableCell align="right">Acción</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {data.pendientes.map(n => (
                  <TableRow key={n.empresaId} hover>
                    <TableCell>
                      <Typography variant="body2" fontWeight={600} component="div">
                        {n.nombreComercial || n.razonSocial}
                        {n.producto === 'HOLAVECI_LOCAL' && (
                          <Chip label="Solo Local" size="small" color="secondary" sx={{ ml: 1, height: 20 }} />
                        )}
                      </Typography>
                      {n.nombreComercial && n.nombreComercial !== n.razonSocial && (
                        <Typography variant="caption" color="text.secondary">{n.razonSocial}</Typography>
                      )}
                    </TableCell>
                    <TableCell>{n.nit}</TableCell>
                    <TableCell>{n.guiaCodigo || '—'}</TableCell>
                    <TableCell>{chipEstado(n.estadoValidacion)}</TableCell>
                    <TableCell align="center">{n.totalDocumentos}</TableCell>
                    <TableCell align="center">
                      {n.documentosPendientes > 0
                        ? <Chip label={n.documentosPendientes} color="warning" size="small" />
                        : n.documentosPendientes}
                    </TableCell>
                    <TableCell align="center">
                      {n.requiereFotosGuia === false
                        ? <Chip label="No aplica" size="small" variant="outlined" />
                        : n.fotosGuia >= 2
                          ? <Chip label={n.fotosGuia} color="success" size="small" />
                          : <Chip label={`${n.fotosGuia}/2`} color="error" size="small" />}
                    </TableCell>
                    <TableCell>
                      <Typography variant="caption">
                        {fechaLocal(n.fechaCreacion)}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Button size="small" variant="contained"
                        onClick={() => navigate(`/admin/negocios/${n.empresaId}/validar`,
                          { state: { from: '/admin/validacion' } })}>
                        Validar
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </Box>
  );
}
